import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCheck, Play, RotateCcw, SquareMousePointer, Trash2, X } from "lucide-react";
import { useMemo, useState } from "react";

import DialogShell from "../components/DialogShell";
import OverlayShell from "../components/OverlayShell";
import MediaTile from "../components/MediaTile";
import QueryState from "../components/QueryState";
import { errorMessageOf } from "../lib/auth";
import { useConfirm } from "../lib/confirm";
import { daysUntilPurge, RETENTION_DAYS } from "../lib/media/retention";
import { useSignedUrls } from "../lib/media/useSignedUrls";
import { queryKeys } from "../lib/queryKeys";
import { useSelection } from "../lib/useSelection";
import { useToast } from "../lib/toast";
import {
  fetchDeletedMedia,
  purgeExpired,
  purgeMedia,
  restoreMedia,
  type TrashItem,
} from "../lib/media/trash";

export default function Trash({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const [selectMode, setSelectMode] = useState(false);
  const { selected, toggle, selectAll, clear } = useSelection();
  const [busy, setBusy] = useState(false);
  // 選択モードでないときに1枚を押したら、その1枚をどうするか聞く
  const [picked, setPicked] = useState<TrashItem | null>(null);

  const trash = useQuery({
    queryKey: queryKeys.trash,
    queryFn: async (): Promise<TrashItem[]> => {
      // 期限切れの掃除はゴミ箱を開いたときに行う(→ ADR-0015)。
      // ここで失敗しても一覧は見せる。掃除ができないことと中身が見えないことは別
      try {
        await purgeExpired();
      } catch (e) {
        toast(`期限切れの削除に失敗しました: ${errorMessageOf(e)}`, "error");
      }
      return fetchDeletedMedia();
    },
  });

  const items = useMemo(() => trash.data ?? [], [trash.data]);
  const thumbKeys = useMemo(() => items.map((item) => item.thumbnail_key), [items]);
  const { urls: thumbUrls, refresh: refreshThumbs } = useSignedUrls(thumbKeys);

  async function refreshAll() {
    clear();
    await queryClient.invalidateQueries({ queryKey: queryKeys.trash });
    await queryClient.invalidateQueries({ queryKey: queryKeys.timelines });
    await queryClient.invalidateQueries({ queryKey: queryKeys.monthCounts });
  }

  const chosen = items.filter((item) => selected.has(item.id));

  /** 選択分・全件のどちらも同じ流れなので、対象だけ受け取る */
  async function restoreMany(targets: TrashItem[]) {
    const count = targets.length;
    if (count === 0) return;
    const ok = await confirm({
      message: `${String(count)}件を復元します。`,
      confirmLabel: "復元",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await restoreMedia(targets.map((item) => item.id));
      setSelectMode(false);
      await refreshAll();
      toast(`${String(count)}件を復元しました。`);
    } catch (e) {
      // 詳細はユーザーに出さない。原因はコンソールで追う
      console.error("復元に失敗", e);
      toast(`${String(count)}件の復元に失敗しました。`, "error");
    } finally {
      setBusy(false);
    }
  }

  async function purgeMany(targets: TrashItem[]) {
    const count = targets.length;
    if (count === 0) return;
    const ok = await confirm({
      message: `${String(count)}件を完全に削除します。\n※この操作は取り消せません。`,
      confirmLabel: "削除",
      tone: "danger",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await purgeMedia(targets);
      setSelectMode(false);
      await refreshAll();
      toast(`${String(count)}件を完全に削除しました。`);
    } catch (e) {
      console.error("完全削除に失敗", e);
      toast(`${String(count)}件の完全削除に失敗しました。`, "error");
    } finally {
      setBusy(false);
    }
  }

  async function restoreOne(item: TrashItem) {
    setPicked(null);
    setBusy(true);
    try {
      await restoreMedia([item.id]);
      await refreshAll();
      toast("復元しました。");
    } catch (e) {
      console.error("復元に失敗", e);
      toast("復元に失敗しました。", "error");
    } finally {
      setBusy(false);
    }
  }

  async function purgeOne(item: TrashItem) {
    setPicked(null);
    const ok = await confirm({
      message: "完全に削除します。\n※この操作は取り消せません。",
      confirmLabel: "削除",
      tone: "danger",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await purgeMedia([item]);
      await refreshAll();
      toast("完全に削除しました。");
    } catch (e) {
      console.error("完全削除に失敗", e);
      toast("完全削除に失敗しました。", "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <OverlayShell title="ゴミ箱" icon={Trash2} onClose={onClose} width="grid">
      {/* 選択中は下部バーが被さるぶんの逃げ幅を足す(PC はバーがカード内に収まる) */}
      <div className={`flex w-full flex-col gap-3 p-4 ${selectMode ? "pb-24 xl:pb-4" : ""}`}>
        {/* PC は説明文と選択ボタンを同じ行に(左右に振り分け)。スマホは説明文だけ。
            PC ではグリッドを送っても操作が手元に残るよう、行ごと上に貼り付ける
            (見出しは xl ではスクロールの外にあるので重ならない) */}
        <div className="flex flex-col gap-3 xl:sticky xl:top-0 xl:z-10 xl:-mx-4 xl:-mt-4 xl:flex-row xl:items-center xl:justify-between xl:bg-[var(--color-bg)] xl:px-4 xl:pt-4 xl:pb-3">
          <p className="text-xs text-[var(--color-sub)]">
            削除した写真・動画は{RETENTION_DAYS}日間ここに残り、その後で完全に削除されます。
          </p>

          {items.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setSelectMode((current) => !current);
                clear();
              }}
              aria-pressed={selectMode}
              className={`hidden shrink-0 items-center gap-1.5 rounded-lg px-3 py-2 text-sm transition xl:flex ${
                selectMode
                  ? "bg-[var(--color-sub)] font-medium text-[var(--color-bg)] hover:opacity-85"
                  : "border border-[var(--color-line)] bg-white text-[var(--color-ink)] hover:bg-[color-mix(in_srgb,var(--color-ink)_5%,transparent)]"
              }`}
            >
              <SquareMousePointer className="size-4" aria-hidden />
              {selectMode ? "選択をやめる" : "選択"}
            </button>
          )}
        </div>

        <QueryState
          isPending={trash.isPending}
          isError={trash.isError}
          errorMessage="ゴミ箱の取得に失敗しました。"
          isEmpty={trash.isSuccess && items.length === 0}
          emptyMessage="ゴミ箱は空です。"
          emptyClassName="p-10 text-center text-sm"
        />

        <div className="grid grid-cols-3 gap-1.5 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-[repeat(auto-fill,minmax(180px,1fr))]">
          {items.map((item) => (
            <MediaTile
              key={item.id}
              thumbUrl={thumbUrls[item.thumbnail_key]}
              onThumbError={refreshThumbs}
              showCheck={selectMode}
              isSelected={selected.has(item.id)}
              dimmed
              onClick={() => {
                if (selectMode) toggle(item.id);
                else setPicked(item);
              }}
            >
              {item.type === "video" && (
                <span className="absolute top-1.5 left-1.5 rounded bg-black/55 p-0.5">
                  <Play className="size-2.5 fill-current text-white" aria-hidden />
                </span>
              )}
              <span className="absolute inset-x-0 bottom-0 bg-black/45 py-0.5 text-[10px] text-white">
                あと{daysUntilPurge(item.deleted_at)}日
              </span>
            </MediaTile>
          ))}
        </div>
      </div>

      {items.length > 0 && (
        <div
          className={`pointer-events-none fixed inset-x-0 z-10 flex justify-end px-4 xl:hidden ${
            selectMode
              ? "bottom-[calc(5rem+env(safe-area-inset-bottom))]"
              : "bottom-[calc(1rem+env(safe-area-inset-bottom))]"
          }`}
        >
          <button
            type="button"
            onClick={() => {
              setSelectMode((current) => !current);
              clear();
            }}
            aria-pressed={selectMode}
            aria-label={selectMode ? "選択をやめる" : "選択"}
            className={`pointer-events-auto flex size-14 items-center justify-center rounded-full shadow-lg ${
              selectMode
                ? "bg-[var(--color-sub)] text-[var(--color-bg)]"
                : "border border-[var(--color-line)] bg-white text-[var(--color-ink)]"
            }`}
          >
            {selectMode ? (
              <X className="size-6" aria-hidden />
            ) : (
              <SquareMousePointer className="size-6" aria-hidden />
            )}
          </button>
        </div>
      )}

      {selectMode && (
        // 項目は固定(件数・復元・すべて選択・完全に削除)。やめる操作は選択ボタン側が持つ。
        // 全画面のスマホ・タブレットはスクロールに関わらず画面下部に留める(fixed)。
        // モーダルの PC はカードの中に収める(sticky)
        <div className="fixed inset-x-0 bottom-0 z-10 flex items-center justify-center gap-2 border-t border-[var(--color-line)] bg-white p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] xl:sticky xl:mt-auto">
          <span className="text-xs text-[var(--color-sub)] tabular-nums">{selected.size}件</span>
          <button
            type="button"
            onClick={() => {
              // 全件選んだ状態でもう一度押すと、選び直しやすいよう全解除に切り替わる
              if (selected.size === items.length) clear();
              else selectAll(items.map((item) => item.id));
            }}
            className="flex items-center gap-1 rounded-lg border border-[var(--color-line)] px-3 py-2 text-sm transition-colors hover:bg-[color-mix(in_srgb,var(--color-ink)_5%,transparent)]"
          >
            <CheckCheck className="size-4" aria-hidden />
            {selected.size === items.length ? "選択を解除" : "すべて選択"}
          </button>
          <button
            type="button"
            onClick={() => void restoreMany(chosen)}
            disabled={busy || selected.size === 0}
            className="flex items-center gap-1 rounded-lg border border-[var(--color-line)] px-3 py-2 text-sm transition-colors enabled:hover:bg-[color-mix(in_srgb,var(--color-ink)_5%,transparent)] disabled:opacity-50"
          >
            <RotateCcw className="size-4" aria-hidden />
            復元
          </button>
          <button
            type="button"
            onClick={() => void purgeMany(chosen)}
            disabled={busy || selected.size === 0}
            className="flex items-center gap-1 rounded-lg bg-[var(--color-plum)] px-3 py-2 text-sm text-white transition enabled:hover:opacity-85 disabled:opacity-50"
          >
            <Trash2 className="size-4" aria-hidden />
            完全に削除
          </button>
        </div>
      )}

      {/* 1枚だけの操作。確認ダイアログと同じ姿(→ DialogShell)で、行き先だけ3択にする */}
      {picked !== null && (
        <DialogShell>
          <p className="text-sm text-[var(--color-ink)]">
            この{picked.type === "video" ? "動画" : "写真"}をどうしますか？
          </p>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              // 完全削除も並ぶ問いなので、Enter 一発で実行されないよう焦点はこちらに
              autoFocus
              onClick={() => {
                setPicked(null);
              }}
              className="rounded-lg px-3 py-2 text-sm text-[var(--color-sub)] transition-colors hover:bg-[color-mix(in_srgb,var(--color-ink)_6%,transparent)]"
            >
              キャンセル
            </button>
            <button
              type="button"
              onClick={() => void restoreOne(picked)}
              className="rounded-lg bg-[var(--color-accent)] px-3 py-2 text-sm text-white transition hover:opacity-85"
            >
              復元
            </button>
            <button
              type="button"
              onClick={() => void purgeOne(picked)}
              className="rounded-lg bg-[var(--color-plum)] px-3 py-2 text-sm text-white transition hover:opacity-85"
            >
              完全に削除
            </button>
          </div>
        </DialogShell>
      )}
    </OverlayShell>
  );
}
